import { useState, useEffect } from "react";
import { groupBy as rowGrouper } from "lodash-es";
import { TreeDataGrid } from "react-data-grid";
import { getRawRecords, getSiteCurr } from "../../services/managment";
import { LabColumnsNoSelected, OtherColumnsNoSelected } from "../../data/RegionData";
import { columns } from "../../data/RecordData";
import { CircularProgress, Box, Stack, Modal, Typography, TextField, MenuItem } from "@mui/material";
import { DataGrid as DataGridMUI, GridToolbar } from "@mui/x-data-grid";
import { Button, Dialog, DialogTitle, DialogContent, FormControl, InputLabel, Select } from "@mui/material";
import { hasAnyPermission, useAuth } from "../../services/auth";

function rowKeyGetter(row) {
  return row.softid;
}
const MLDataMUI = ({
  softid,
  soilType,
  algo,
  dataSelected,
  inputPHH2O = '',
  inputCEC = '',
  inputClay = '',
  inputOM = ''
}) => {
  const [rows, setRows] = useState([]);
  const [isloading, setLoading] = useState(true);
  const formatDate = (date) => new Date(date).toISOString();

  const buildFilename = () => {
    const parts = [];
    if (dataSelected?.farm_name) parts.push(dataSelected.farm_name);
    if (dataSelected?.region_name) parts.push(dataSelected.region_name);
    if (dataSelected?.softid) parts.push(dataSelected.softid);
    parts.push(formatDate(new Date()));
    return parts.join("-");
  };
  const ML_BASE_URL = "https://ml.enfarm.com"; //process.env.REACT_APP_ML_BASE_URL ||
  const filename = buildFilename();

  useEffect(() => {
    if (!softid || !soilType || !algo) return;
    setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);

    // Chuẩn bị payload
    const payload = {};
    if (inputPHH2O !== '') payload.pHH2O = Number(inputPHH2O);
    if (inputCEC !== '') payload.CEC = Number(inputCEC);
    if (inputClay !== '') payload.Clay = Number(inputClay);
    if (inputOM !== '') payload.OM = Number(inputOM);

    fetch(`${ML_BASE_URL}/query-${algo}?soil_type=${soilType}&softid=${softid}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: Object.keys(payload).length > 0 ? JSON.stringify(payload) : null,
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        setRows(data);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") {
          alert("Timeout: API ML quá lâu!");
        }
        setLoading(true);
      })
      .finally(() => clearTimeout(timeoutId));

    return () => clearTimeout(timeoutId);
  }, [softid, soilType, algo, dataSelected, inputPHH2O, inputCEC, inputClay, inputOM]);

  // Tùy chỉnh columns cho ML nếu cần
  const mlColumns = [
    { field: "created_at", headerName: "Thời gian", width: 180 },
    { field: "t_raw", headerName: "T-RAW", width: 100 },
    { field: "m_raw", headerName: "M-RAW", width: 100 },
    { field: "ec_raw", headerName: "EC-RAW", width: 100 },
    { field: "ph_raw", headerName: "PH-RAW", width: 100 },
    // Thêm 4 trường đặc biệt
    { field: "OM", headerName: "OM", width: 100 },
    { field: "Clay", headerName: "Clay", width: 100 },
    { field: "pHH2O", headerName: "pHH2O", width: 100 },
    { field: "CEC", headerName: "CEC", width: 100 },
    // Thêm các trường reg_*
    ...Object.keys(rows[0] || {}).filter(k => k.startsWith("reg_")).map(k => ({
      field: k, headerName: k.toUpperCase(), width: 120
    })),
    // Các trường đặc biệt cần hiển thị
    { field: "m_ml", headerName: "M-ML", width: 120 },
    { field: "f_m", headerName: "F(M-ML)", width: 100 },
    { field: "f_ph", headerName: "F(REG_PH)", width: 100 },
    { field: "inst_N", headerName: "inst_N", width: 120 },
    { field: "inst_P", headerName: "inst_P", width: 120 },
    { field: "inst_K", headerName: "inst_K", width: 120 },

    // Thêm các trường gb_*, xgb_*, npk_v1_ nếu muốn
    ...Object.keys(rows[0] || {}).filter(k =>
      k.startsWith(algo + "_") || (algo === "npk-v1" && k.startsWith("npk_v1_"))
    ).map(k => ({
      field: k, headerName: k.toUpperCase(), width: 120
    }))
  ];

  return (
    <>
      {isloading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGridMUI
            rows={rows.map((row, idx) => ({ id: idx, ...row }))}
            columns={mlColumns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
            }}
            pageSizeOptions={[20]}
            checkboxSelection
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                csvOptions: { fileName: filename },
                printOptions: { fileName: filename },
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

const RawDataMUI = ({ softid, dataSelected }) => {
  const [rows, setRows] = useState([]);
  const [isloading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const formatDate = (date) => new Date(date).toISOString();

  const buildFilename = () => {
    const parts = [];
    if (dataSelected?.farm_name) parts.push(dataSelected.farm_name);
    if (dataSelected?.region_name) parts.push(dataSelected.region_name);
    if (dataSelected?.softid) parts.push(dataSelected.softid);
    parts.push(formatDate(new Date()));
    return parts.join("-");
  };

  const filename = buildFilename();

  useEffect(() => {
    if (!softid) return;

    setLoading(true);
    setError("");

    getRawRecords({ softid })
      .then((res) => {
        const mappedData = Array.isArray(res) ? res.map((it) => ({ ...it, ...dataSelected })) : [];
        setRows(mappedData);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setRows([]);
        setError("Không tải được dữ liệu raw.");
        setLoading(false);
      });
  }, [softid, dataSelected]);

  return (
    <>
      {isloading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGridMUI
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
            }}
            pageSizeOptions={[20]}
            checkboxSelection
            disableRowSelectionOnClick
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                csvOptions: { fileName: filename },
                printOptions: { fileName: filename },
              },
            }}
          />
        </Box>
      )}
    </>
  );
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const FarmGrouping = ({ records, groupby_cols, columns, onRowClick, searchQuery, setSearchQuery }) => {
  const options = groupby_cols;
  const defaultGroupOption = options[0];
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const [methodFilter, setMethodFilter] = useState('all');

  const filteredRecords = records.filter((record) => {
    const searchLower = searchQuery?.toLowerCase() || "";
    const searchMatch =
      record.farm_name?.toLowerCase().includes(searchLower) ||
      record.region_name?.toLowerCase().includes(searchLower) ||
      record.owner_phone?.toLowerCase().includes(searchLower) ||
      record.QrSensor?.toLowerCase().includes(searchLower) ||
      record.QrBox?.toLowerCase().includes(searchLower) ||
      record.QrGateway?.toLowerCase().includes(searchLower);
    const methodMatch =
      methodFilter === 'all' ||
      (methodFilter === 'ble' && record.method === 0) ||
      (methodFilter === 'ln' && record.method === 1);
    return searchMatch && methodMatch;
  });

  function toggleOption(option, enabled) {
    const index = selectedOptions.indexOf(option);
    if (enabled) {
      if (index === -1) {
        setSelectedOptions((options) => [...options, option]);
      }
    } else if (index !== -1) {
      setSelectedOptions((options) => {
        const newOptions = [...options];
        newOptions.splice(index, 1);
        return newOptions;
      });
    }
    setExpandedGroupIds(new Set());
  }

  useEffect(() => {
    setSelectedOptions(defaultGroupOption ? [defaultGroupOption] : []);
  }, [defaultGroupOption]);

  const labelOption = [
    { key: "farm_name", label: "Tên trang trại" },
    { key: "region_name", label: "Điểm đo" },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ marginBottom: "10px" }}
      >
        <Box>
          <b>Group by:</b>
          <Stack sx={{ gap: "10px" }} direction="row">
            {options.map((option) => (
              <label key={option}>
                <input
                  type="checkbox"
                  checked={selectedOptions?.includes(option)}
                  onChange={(event) => toggleOption(option, event.target.checked)}
                  sx={{ gap: "5px" }}
                />
                {labelOption?.find((it) => it.key === option)?.label}
              </label>
            ))}
          </Stack>
        </Box>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label="Method"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            size="small"
            sx={{ width: '120px' }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="ble">BLE</MenuItem>
            <MenuItem value="ln">LN</MenuItem>
          </TextField>
          <TextField
            label="Search by Farm or Region"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ width: { xs: "100%", sm: "250px", md: "300px" } }}
          />
        </Stack>
      </Stack>

      <Box sx={{ padding: "10px" }}>
        <TreeDataGrid
          columns={columns}
          rows={filteredRecords}
          rowKeyGetter={rowKeyGetter}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          groupBy={selectedOptions}
          rowGrouper={rowGrouper}
          expandedGroupIds={expandedGroupIds}
          onExpandedGroupIdsChange={setExpandedGroupIds}
          defaultColumnOptions={{
            resizable: true,
            draggable: true,
            sortable: true,
          }}
          renderers={{ renderCheckbox }}
          onCellDoubleClick={(params) => onRowClick(params.row)}
        />
      </Box>
    </Box>
  );
};

function renderCheckbox({ onChange, ...props }) {
  return null;
}

const CultivateMonitorDash = ({ where }) => {
  const [records, setRecords] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const group_list = where === 0 ? ["region_name"] : ["farm_name", "region_name"];
  const columns = where === 0 ? LabColumnsNoSelected : OtherColumnsNoSelected;

  const [softid, setsoftid] = useState(null);
  const [farmName, setfarmName] = useState(null);
  const [regionName, setregionName] = useState(null);
  const [indepth, setindepth] = useState(null);
  const [dataSelected, setDataSelected] = useState();
  const [open, setOpen] = useState(false);

  const [dataMode, setDataMode] = useState("raw"); // "raw" hoặc "ml"
  const [mlAlgo, setMlAlgo] = useState("gb"); // "gb" hoặc "xgb"
  const [soilType, setSoilType] = useState(1);
  const [showModeDialog, setShowModeDialog] = useState(false);
  const [mlParams, setMlParams] = useState(null); // Lưu params để truyền cho MLDataMUI khi Done
  const [inputPHH2O, setInputPHH2O] = useState('');
  const [inputCEC, setInputCEC] = useState('');
  const [inputClay, setInputClay] = useState('');
  const [inputOM, setInputOM] = useState('');

  const [user] = useAuth();
  const canUseMlPrediction = hasAnyPermission(user, [
    "soil_sample_edit",
    "soil_sample_manage",
    "benchmark_edit",
    "benchmark_manage",
  ]);

  const HandleClickOpen = (record) => {
    setsoftid(record?.softid);
    setregionName(record?.region_name);
    setfarmName(record?.farm_name);
    setindepth(record?.in_depth);
    setDataSelected(record);
    setShowModeDialog(true); // show chọn raw/ml trước
  };

  const handleModeSelect = (mode) => {
    setDataMode(mode);
    setShowModeDialog(false);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setShowModeDialog(false);
    setsoftid(null);
    setregionName(null);
    setfarmName(null);
    setindepth(null);
    setDataSelected(null);
    setMlParams(null);
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");

    getSiteCurr({ where })
      .then((res) => {
        if (!mounted) return;
        setRecords(Array.isArray(res) ? res : []);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        if (!mounted) return;
        setRecords([]);
        setError("Không tải được dữ liệu site hiện tại.");
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [where]);

  return (
    <>
      <Box>
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <FarmGrouping
            records={records}
            groupby_cols={group_list}
            columns={columns}
            onRowClick={HandleClickOpen}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        )}
      </Box>
      {/* Dialog chọn raw/ml */}
      <Dialog open={showModeDialog} onClose={handleCloseModal}>
        <DialogTitle>Chọn loại dữ liệu</DialogTitle>
        <DialogContent>
          <Button variant="contained" onClick={() => handleModeSelect("raw")} sx={{ m: 1 }}>
            Raw
          </Button>
          {canUseMlPrediction && (
            <Button variant="contained" onClick={() => handleModeSelect("ml")} sx={{ m: 1 }}>
              ML (Dự đoán)
            </Button>
          )}
        </DialogContent>
      </Dialog>
      {/* Modal hiển thị dữ liệu */}
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" align="center">
            Trang trại: {farmName} - Điểm đo: {regionName} - Độ sâu: {indepth}
          </Typography>
          {dataMode === "raw" ? (
            <RawDataMUI softid={softid} dataSelected={dataSelected} />
          ) : (
            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <FormControl>
                  <InputLabel id="soil-type-label">Soil Type</InputLabel>
                  <Select
                    labelId="soil-type-label"
                    value={soilType}
                    label="Soil Type"
                    onChange={(e) => setSoilType(Number(e.target.value))}
                    sx={{ minWidth: 100 }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map((val) => (
                      <MenuItem key={val} value={val}>{val}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel id="ml-algo-label">Thuật toán</InputLabel>
                  <Select
                    labelId="ml-algo-label"
                    value={mlAlgo}
                    label="Thuật toán"
                    onChange={(e) => setMlAlgo(e.target.value)}
                    sx={{ minWidth: 100 }}
                  >
                    <MenuItem value="gb">GB</MenuItem>
                    <MenuItem value="xgb">XGB</MenuItem>
                    <MenuItem value="npk-v1">NPK V1</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="pHH2O"
                  value={inputPHH2O}
                  onChange={e => setInputPHH2O(e.target.value)}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                />
                <TextField
                  label="CEC"
                  value={inputCEC}
                  onChange={e => setInputCEC(e.target.value)}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                />
                <TextField
                  label="Clay"
                  value={inputClay}
                  onChange={e => setInputClay(e.target.value)}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                />
                <TextField
                  label="OM"
                  value={inputOM}
                  onChange={e => setInputOM(e.target.value)}
                  size="small"
                  sx={{ width: 100 }}
                  type="number"
                />
                <Button
                  variant="contained"
                  onClick={() => setMlParams({ softid, soilType, algo: mlAlgo, dataSelected })}
                  sx={{ ml: 2 }}
                >
                  Done
                </Button>
              </Stack>
              {mlParams && (
                <MLDataMUI
                  softid={mlParams.softid}
                  soilType={mlParams.soilType}
                  algo={mlParams.algo}
                  dataSelected={mlParams.dataSelected}
                  inputPHH2O={inputPHH2O}
                  inputCEC={inputCEC}
                  inputClay={inputClay}
                  inputOM={inputOM}
                />
              )}
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default CultivateMonitorDash;
